import { getDay, getEthiopianMonth, toEthiopianTimeWithSuffix } from "@/lib/ethiopian-date-conversion";
import { useMutation, useQuery } from "@tanstack/react-query";
import { toEthiopian } from "ethiopian-calendar-new";
import { useMemo, useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner"
import { User } from "./auth-provider";
import Cookies from "js-cookie";
import { AlertCircle, CheckCircle2 } from "lucide-react";
import { useServerTime } from "@/utils/server-time";
import { useBookingStore } from "@/store/booking-store";
import { useCbeSuperAppPayment } from "@/hooks/useCbeSuperAppPayment";
import { useCbeBirrPlusPayment } from "@/hooks/useCbeBirrPlusPayment";
import {
  detectPaymentProvider,
  getOpenedInLabelKey,
  isBrowserC2bPaymentMethod,
  PAYMENT_METHOD_IDS,
  shouldAutoSelectPaymentMethod,
} from "@/lib/payment-provider";
import { isCbePaymentSuccess } from "@/lib/cbe-superapp";
import { isCbeBirrPlusPaymentSuccess } from "@/lib/cbe-birr-plus";
import { cbeDebugError, cbeDebugLog } from "@/lib/cbe-debug";

declare global {
  interface Window {
    consumerapp: any;
    handleinitDataCallback: any;
  }
}

type BookingSummaryModalProp = {
  booking: { branch_id?: string; branch_name?: string; service_name?: string; date?: string; time_from?: string; time_to?: string; service_id?: string; duration_id?: string },
  appointerUser: User,
  bookingTab: "self" | "other"
  setSummaryOpen: (o: boolean) => void
  mode?: "booking" | "reschedule"
  appointmentId?: string
}

export default function BookingSummaryModal({ booking, appointerUser, bookingTab, setSummaryOpen, mode = "booking", appointmentId }: BookingSummaryModalProp) {
  const { t, i18n } = useTranslation();
  const { serverDate } = useServerTime();
  const { calendarType } = useBookingStore();
  const [documents, setDocuments] = useState<number>(0)
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<string | null>(null)
  const [bookedSuccessfully, setBookedSuccessfully] = useState(false)
  const bookingCompletedRef = useRef(false)
  const fixedAmount = 50
  const mpesaAmount = "50"
  const [paymentProvider, setPaymentProvider] = useState(() => detectPaymentProvider())
  const { pay: payWithCbe, available: cbeAvailable } = useCbeSuperAppPayment({
    // Backend generates xAccessToken — do not block pay on SDK fetchAccessToken
    autoFetchToken: false,
    preferSdkAccessToken: false,
    appName: "Qetero",
  })
  const { pay: payWithCbeBirr, available: cbeBirrAvailable } = useCbeBirrPlusPayment()

  const isPLC = booking?.service_name?.toLowerCase().includes("plc");

  useEffect(() => {
    const provider = detectPaymentProvider()
    console.log("[CBE Birr Plus] modal provider effect", { provider, cbeBirrAvailable, cbeAvailable })
    setPaymentProvider(provider)
  }, [cbeBirrAvailable, cbeAvailable])

  const completeInAppBooking = () => {
    if (bookingCompletedRef.current) return
    bookingCompletedRef.current = true
    setBookedSuccessfully(true)
    window.setTimeout(() => {
      window.location.href = "/booking/history"
    }, 1500)
  }






  const dateObj = useMemo(() => (booking?.date ? new Date(booking.date) : serverDate), [booking?.date, serverDate])
  const { year, month, day } = toEthiopian(dateObj.getFullYear(), dateObj.getMonth() + 1, dateObj.getDate());
  const amDate = `${t(getDay(dateObj.getDay(), "am"))}፣ ${getEthiopianMonth(month)} ${day}፣ ${year} `
  const enDate = dateObj.toLocaleDateString(undefined, { weekday: "long", year: "numeric", month: "short", day: "2-digit" })
  const isAmharic = i18n.language?.toLowerCase().startsWith("am")
  const dateTop = isAmharic ? amDate : enDate
  const dateBottom = isAmharic ? enDate : amDate
  const { localTime, gregorianTime } = useMemo(() => {
    if (!booking?.time_from || !booking?.time_to) return { localTime: "", gregorianTime: "" }
    const [fh, fm] = booking.time_from.split(":").map(Number)
    const hour12 = fh % 12 || 12
    const amPm = fh >= 12 ? "PM" : "AM"
    const gregorianTimeStr = `${hour12.toString().padStart(2, "0")}:${fm.toString().padStart(2, "0")} ${amPm}`
    const time12h = `${hour12}:${fm.toString().padStart(2, "0")}`
    const localTimeStr = toEthiopianTimeWithSuffix(time12h, amPm)
    return { localTime: localTimeStr, gregorianTime: gregorianTimeStr }
  }, [booking?.time_from, booking?.time_to])
  const timeTop = isAmharic ? localTime : gregorianTime
  const timeBottom = isAmharic ? gregorianTime : localTime





  const { data: paymentOptions, isLoading: paymentLoading } = useQuery({
    queryKey: ["paymentMethods"],
    queryFn: async () => {
      const res = await fetch("https://pay.cheche.et/api/v1/payments/payment-services/QETERO")
      const data = await res.json()
      return data.data || []
    },
  })






  /** ------------------ MUTATION: Add Appointment ------------------ **/
  const addAppointmentMutation = useMutation({
    mutationFn: async () => {
      const [hours, minutes] = booking.time_from?.split(":").map(Number) ?? [0, 0]
      const date = new Date(booking.date ?? "")
      date.setHours(hours, minutes, 0, 0)

      let appointmentData: any = {
        first_name: appointerUser.firstname,
        last_name: appointerUser.lastname,
        mobile: normalizeMobileNumber(appointerUser.mobile),
        appointment_time: date.toISOString(),
        document_count: documents,
        reschedule_count: 0,
        appointment_through: bookingTab === 'self' ? "self" : "others",
        branch_service_id: booking.service_id,
        appointment_date: date.toISOString().split("T")[0],
        appointment_duration_id: booking.duration_id,
        created_by: appointerUser?.id,
      }

      console.log('appointment data', appointmentData)

      if (mode === "reschedule") {
        appointmentData = {
          appointmentId: appointmentId,
          branch_id: booking.branch_id,
          branch_service_id: booking.service_id,
          created_by: appointerUser?.id,
          newAppointmentDate: date.toISOString().split("T")[0],
          newDurationId: booking.duration_id
        }
      }

      const apiUrl = import.meta.env.VITE_API_URL || import.meta.env.NEXT_PUBLIC_API_URL || ''
      const endpoint = mode === "reschedule"
        ? `${apiUrl}/v1/appointments/self/reschedule`
        : `${apiUrl}/v1/appointments/${bookingTab === 'self' ? "add_appointment" : "add_appointment_for_other"}`;

      const token = Cookies.get("qetero.token");

      const res = await fetch(
        endpoint,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {})
          },
          body: JSON.stringify(appointmentData),
        }
      )

      const data = await res.json()
      if (data?.status === "success") {
        if (mode === "reschedule") {
          toast.success(t("rescheduleSuccessful"));
          setSummaryOpen(false);
          // Redirect to history or home
          window.location.href = "/booking/history";
          return data;
        }

        toast.success(t("appointmentCreatedRedirecting"));
        const appointmentId = data?.data?.data?.id
        await initiatePaymentMutation.mutateAsync(appointmentId)
      } else {
        if (typeof data?.statusCode === 'string') {
          if (data.statusCode.includes("Maximum reschedule limit reached")) {
            toast.error(t("maxRescheduleLimitReached"))
          } else if (data.statusCode.includes("Name validation failed")) {
            toast.error(t("nameValidationFailed"))
          } else {
            toast.error(data?.message || t("failedToCreateAppointment"))
          }
        } else {
          toast.error(data?.message || t("failedToCreateAppointment"))
        }
      }

      return data
    },
    onError: (error: any) => {
      console.error("Error creating appointment:", error)
      toast.error(t("errorCreatingAppointment"))
    },
  })


  /** ------------------ MUTATION: Initiate Payment ------------------ **/
  const initiatePaymentMutation = useMutation({
    mutationFn: async (appointmentId: string) => {
      const provider = detectPaymentProvider()
      console.log("[CBE Birr Plus] initiatePayment provider", { provider, appointmentId, cbeBirrAvailable, cbeAvailable })

      // 1) CBE Birr Plus — backend /auth/pay then window.myJsChannel.postMessage (native popup)
      if (provider === "cbe_birr") {
        console.log("[CBE Birr Plus] modal pay start", {
          appointmentId,
          amount: fixedAmount,
          cbeBirrAvailable,
          myJsChannel: (window as any).myJsChannel,
        })
        cbeDebugLog("modal CBE Birr Plus pay start", {
          appointmentId,
          amount: fixedAmount,
          cbeBirrAvailable,
        })

        if (!cbeBirrAvailable) {
          console.error("[CBE Birr Plus] modal pay blocked — myJsChannel unavailable")
          cbeDebugError("modal CBE Birr Plus pay blocked — myJsChannel unavailable")
          toast.error(t("openInCbeBirrPlus"))
          throw new Error("CBE_BIRR_PLUS_UNAVAILABLE")
        }

        try {
          const result = await payWithCbeBirr({
            transactionId: appointmentId,
            amount: fixedAmount,
            title: "Ticket payment",
          })

          console.log("[CBE Birr Plus] modal pay result", result)
          cbeDebugLog("modal CBE Birr Plus pay result", result)

          if (isCbeBirrPlusPaymentSuccess(result)) {
            console.log("[CBE Birr Plus] modal pay SUCCESS — redirecting")
            toast.success(t("paymentSuccessful") || t("paymentSuccess"))
            window.location.href = `/booking/success?appointment_id=${appointmentId}`
            return result
          }

          console.error("[CBE Birr Plus] modal pay finished without confirmation", result)
          cbeDebugError("modal CBE Birr Plus pay finished without confirmation", result)
          toast.error(t("paymentFailed"))
          throw new Error("CBE_BIRR_PAYMENT_FAILED")
        } catch (error: any) {
          console.error("[CBE Birr Plus] modal pay error", error?.message, error)
          cbeDebugError("modal CBE Birr Plus pay error", {
            message: error?.message,
            name: error?.name,
            error,
          })
          if (error?.message === "CBE_BIRR_PLUS_UNAVAILABLE") {
            toast.error(t("openInCbeBirrPlus"))
          } else if (String(error?.message || "").includes("CBE_BIRR_UNAUTHORIZED")) {
            toast.error(t("cbeBirrUnauthorized"))
          } else {
            toast.error(error?.message || t("paymentInitiationFailed"))
          }
          throw error
        }
      }

      // 2) CBE SuperApp — createOrder via pay_api + initiatePayment bridge
      if (provider === "cbe") {
        cbeDebugLog("modal CBE pay start", {
          appointmentId,
          amount: fixedAmount,
          cbeAvailable,
        })

        if (!cbeAvailable) {
          cbeDebugError("modal CBE pay blocked — bridge unavailable")
          toast.error(t("openInCbeSuperApp"))
          throw new Error("CBE_SUPERAPP_UNAVAILABLE")
        }

        try {
          const result = await payWithCbe({
            transactionId: appointmentId,
            amount: fixedAmount,
            title: "Ticket payment",
          })

          cbeDebugLog("modal CBE pay result", result)

          if (isCbePaymentSuccess(result)) {
            toast.success(t("paymentSuccessful") || t("paymentSuccess"))
            // Webhook is backend-only; refresh status from our API via success page
            window.location.href = `/booking/success?appointment_id=${appointmentId}`
            return result
          }

          cbeDebugError("modal CBE pay finished without ft_number", result)
          toast.error(t("paymentFailed"))
          throw new Error("CBE_PAYMENT_FAILED")
        } catch (error: any) {
          cbeDebugError("modal CBE pay error", {
            message: error?.message,
            name: error?.name,
            error,
          })
          if (error?.message === "CBE_SUPERAPP_UNAVAILABLE") {
            toast.error(t("openInCbeSuperApp"))
          } else {
            toast.error(error?.message || t("paymentInitiationFailed"))
          }
          throw error
        }
      }

      // 3) M-Pesa Mini App — hylid-bridge payWithMpesa
      if (provider === "mpesa") {
        try {
          // For M-Pesa origin, automatically use the specific payment method ID
          const mpesaPaymentMethodId = PAYMENT_METHOD_IDS.mpesa
          
          const paymentPayload = {
            appointment_id: appointmentId,
            type: "service Fee",
            amount: mpesaAmount,
            title: "serviceFee_" + mpesaAmount,
            way: "inApp",
            paymentType: "InApp",
            paymentMethod: mpesaPaymentMethodId,
          }

          const baseApiUrl = import.meta.env.VITE_API_URL || import.meta.env.NEXT_PUBLIC_API_URL || ''
          const apiUrl = `${baseApiUrl}/v1/payments/createChechePayment`

          let res: Response
          try {
            res = await fetch(apiUrl, {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify(paymentPayload),
            })
          } catch (fetchError: any) {
            console.error('[M-Pesa Payment] ❌❌❌ FETCH ERROR ❌❌❌')
            console.error('[M-Pesa Payment] ❌ Fetch Error Type:', fetchError?.constructor?.name)
            console.error('[M-Pesa Payment] ❌ Fetch Error Name:', fetchError?.name)
            console.error('[M-Pesa Payment] ❌ Fetch Error Message:', fetchError?.message)
            console.error('[M-Pesa Payment] ❌ Fetch Error Stack:', fetchError?.stack)
            console.error('[M-Pesa Payment] ❌ Network Error Details:', {
              cause: fetchError?.cause,
              code: fetchError?.code,
              errno: fetchError?.errno,
              syscall: fetchError?.syscall,
            })
            console.error('[M-Pesa Payment] ❌ Request Details:', {
              url: apiUrl,
              method: 'POST',
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify(paymentPayload),
            })
            console.error('[M-Pesa Payment] ❌ Possible Causes:')
            console.error('[M-Pesa Payment]   - CORS issue (check server CORS headers)')
            console.error('[M-Pesa Payment]   - Network connectivity issue')
            console.error('[M-Pesa Payment]   - API endpoint not reachable')
            console.error('[M-Pesa Payment]   - SSL/TLS certificate issue')
            console.error('[M-Pesa Payment]   - API URL incorrect:', apiUrl)
            
            throw new Error(`Network request failed: ${fetchError?.message || 'Unknown error'}. URL: ${apiUrl}`)
          }

          if (!res.ok) {
            let errorText = ''
            try {
              errorText = await res.text()
              console.error('[M-Pesa Payment] ❌ API Error Response Text:', errorText)
            } catch (textError) {
              console.error('[M-Pesa Payment] ❌ Failed to read error response text:', textError)
            }
            
            console.error('[M-Pesa Payment] ❌ API Error Details:', {
              status: res.status,
              statusText: res.statusText,
              url: res.url,
              errorText,
            })
            
            throw new Error(`API request failed: ${res.status} ${res.statusText}${errorText ? ` - ${errorText}` : ''}`)
          }

          const data = await res.json()

          // Cheche Pay createOrder result. M-Pesa is charged mpesaAmount.
          const createOrderResult =
            data?.result ||
            data?.data?.result ||
            data?.chechePayresult?.result ||
            data?.chechePayresult ||
            data?.data ||
            data

          const rawMpesaParams = createOrderResult?.payWithMpesaParams

          if (!rawMpesaParams?.businessID) {
            console.error('[M-Pesa Payment] createChechePayment missing payWithMpesaParams', data)
            throw new Error('M-Pesa createChechePayment response missing payWithMpesaParams')
          }

          // Only the fields M-Pesa reads. Charge mpesaAmount, not Cheche's service fee.
          const payWithMpesaParams = {
            businessID: rawMpesaParams.businessID,
            billReference: rawMpesaParams.billReference,
            currency: rawMpesaParams.currency || "ETB",
            reason: rawMpesaParams.reason,
            amount: mpesaAmount,
          }

          console.log('[M-Pesa Payment] using Cheche Pay payWithMpesaParams', payWithMpesaParams)

          // Import hylid-bridge
          let hylidBridge
          try {
            hylidBridge = await import('hylid-bridge')
          } catch (importError: any) {
            console.error('[M-Pesa Payment] ❌ Failed to import hylid-bridge:', importError)
            throw new Error(`Failed to import hylid-bridge: ${importError?.message || 'Unknown error'}`)
          }

          const bridgeCall = hylidBridge.call || (window as any).call

          if (typeof bridgeCall !== 'function') {
            const errorMsg = 'hylid-bridge call function not available'
            console.error('[M-Pesa Payment] ❌', errorMsg)
            console.error('[M-Pesa Payment] ❌ Available window properties:', Object.keys(window).filter(k => k.toLowerCase().includes('hylid') || k.toLowerCase().includes('bridge')))
            throw new Error(errorMsg)
          }

          // Call payWithMpesa with Cheche Pay params, charging 50 birr
          return new Promise((resolve, reject) => {
            try {
              bridgeCall('payWithMpesa', {
                businessID: payWithMpesaParams.businessID,
                billReference: payWithMpesaParams.billReference,
                currency: payWithMpesaParams.currency,
                reason: payWithMpesaParams.reason,
                amount: mpesaAmount,
                success: function(res: any) {
                  completeInAppBooking()
                  resolve(res)
                },
                fail: function(res: any) {
                  console.error('[M-Pesa Payment] ❌❌❌ PAYMENT FAILED ❌❌❌')
                  console.error('[M-Pesa Payment] ❌ Failure Response:', JSON.stringify(res, null, 2))
                  console.error('[M-Pesa Payment] ❌ Error Code:', res?.error)
                  console.error('[M-Pesa Payment] ❌ Error Message:', res?.errorMessage)
                  console.error('[M-Pesa Payment] ❌ Full Error Object:', res)
                  
                  // Log error code details
                  if (res?.error) {
                    const errorCode = res.error
                    if (errorCode >= 0 && errorCode <= 99) {
                      console.error('[M-Pesa Payment] ❌ Error Domain: PRECONDITION (0-99)')
                      
                      // Specific handling for error code 7: Identity Validation failed
                      if (errorCode === 7) {
                        console.error('[M-Pesa Payment] ❌ IDENTITY VALIDATION ERROR (Code 7)')
                        console.error('[M-Pesa Payment] ❌ Possible Causes:')
                        console.error('[M-Pesa Payment]   - User has not completed KYC (Know Your Customer) verification')
                        console.error('[M-Pesa Payment]   - User account is not properly set up for payments')
                        console.error('[M-Pesa Payment]   - User identity information is incomplete or invalid')
                        console.error('[M-Pesa Payment]   - User needs to verify their identity in M-Pesa app')
                        console.error('[M-Pesa Payment]   - Account restrictions or limitations')
                        console.error('[M-Pesa Payment] ❌ User Action Required:')
                        console.error('[M-Pesa Payment]   - User should complete identity verification in M-Pesa app')
                        console.error('[M-Pesa Payment]   - User should check their M-Pesa account status')
                        console.error('[M-Pesa Payment]   - User may need to contact M-Pesa support')
                      }
                    } else if (errorCode >= 100 && errorCode <= 199) {
                      console.error('[M-Pesa Payment] ❌ Error Domain: OPERATION (100-199)')
                    } else if (errorCode >= 200) {
                      console.error('[M-Pesa Payment] ❌ Error Domain: GENERIC (200+)')
                    }
                  }
                  
                  // Provide user-friendly error message based on error code
                  let errorMessage = res?.errorMessage || t("paymentFailed") || "Payment failed"
                  
                  if (res?.error === 7) {
                    errorMessage = t("identityValidationError") || "Identity validation failed. Please complete your identity verification in the M-Pesa app and try again."
                    console.error('[M-Pesa Payment] ❌ Using identity validation error message')
                  }
                  
                  console.error('[M-Pesa Payment] ❌ Displaying error to user:', errorMessage)
                  toast.error(errorMessage)
                  
                  reject(res)
                },
              })
            } catch (callError: any) {
              console.error('[M-Pesa Payment] ❌ Exception during payWithMpesa call:', callError)
              console.error('[M-Pesa Payment] ❌ Error Stack:', callError?.stack)
              console.error('[M-Pesa Payment] ❌ Error Name:', callError?.name)
              console.error('[M-Pesa Payment] ❌ Error Message:', callError?.message)
              reject(callError)
            }
          })
        } catch (error: any) {
          console.error('[M-Pesa Payment] ❌❌❌ PAYMENT INITIATION FAILED ❌❌❌')
          console.error('[M-Pesa Payment] ❌ Error Type:', error?.constructor?.name)
          console.error('[M-Pesa Payment] ❌ Error Message:', error?.message)
          console.error('[M-Pesa Payment] ❌ Error Stack:', error?.stack)
          console.error('[M-Pesa Payment] ❌ Full Error Object:', JSON.stringify(error, Object.getOwnPropertyNames(error), 2))
          console.error('[M-Pesa Payment] ❌ Error Details:', {
            name: error?.name,
            message: error?.message,
            stack: error?.stack,
            cause: error?.cause,
          })
          
          const errorMessage = error?.message || t("paymentInitiationFailed") || "Payment initiation failed"
          console.error('[M-Pesa Payment] ❌ Displaying error to user:', errorMessage)
          toast.error(errorMessage)
          
          throw error
        }
      }

      // 4) Telebirr SuperApp — consumerapp.evaluate(js_fun_start_pay)
      if (provider === "telebirr") {
        window.handleinitDataCallback = function () {
          completeInAppBooking()
        }

        window.BigInt64Array = BigInt64Array || Array

        const baseApiUrl = import.meta.env.VITE_API_URL || import.meta.env.NEXT_PUBLIC_API_URL || ''
        const res = await fetch(`${baseApiUrl}/v1/payments/createChechePayment`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            appointment_id: appointmentId,
            type: "service Fee",
            amount: fixedAmount.toString(),
            title: "serviceFee_" + fixedAmount,
            paymentType: "InApp",
            paymentMethod: PAYMENT_METHOD_IDS.telebirr,
          }),
        })

        const rawRequest = await res.text()
        const obj = JSON.stringify({
          functionName: "js_fun_start_pay",
          params: {
            rawRequest: rawRequest.trim(),
            functionCallBackName: "handleinitDataCallback",
          },
        })

        if (window.consumerapp) {
          window.consumerapp.evaluate(obj)
        }
        return
      }

      // 5) Web C2B — user picks Telebirr or CBE Birr USSD, then Cheche Pay hosted page
      const paymentMethodToUse = selectedPaymentMethod

      const baseApiUrl = import.meta.env.VITE_API_URL || import.meta.env.NEXT_PUBLIC_API_URL || ''
      const res = await fetch(`${baseApiUrl}/v1/payments/createChechePayment`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          appointment_id: appointmentId,
          type: "service Fee",
          amount: fixedAmount.toString(),
          title: "serviceFee_" + fixedAmount,
          paymentType: "C2B",
          paymentMethod: paymentMethodToUse,
        }),
      })
      const data = await res.json()

      const paymentUrl = data?.chechePayresult?.url;
      if (paymentUrl) {
        window.location.href = paymentUrl.trim();
      }

      return data
    },
    onError: (error: any) => {
      console.error('[Payment] ❌❌❌ MUTATION ERROR HANDLER TRIGGERED ❌❌❌')
      console.error('[Payment] ❌ Error Type:', error?.constructor?.name)
      console.error('[Payment] ❌ Error Message:', error?.message)
      console.error('[Payment] ❌ Error Stack:', error?.stack)
      console.error('[Payment] ❌ Full Error Object:', JSON.stringify(error, Object.getOwnPropertyNames(error), 2))
      console.error('[Payment] ❌ Error Details:', {
        name: error?.name,
        message: error?.message,
        stack: error?.stack,
        cause: error?.cause,
        response: error?.response,
        request: error?.request,
      })
      toast.error(t("paymentInitiationFailed"))
    },
  })

  const normalizeMobileNumber = (mobile: string | undefined): string => {
    if (!mobile) return ""

    const cleaned = mobile.trim()

    // If already starts with +251, return as is
    if (cleaned.startsWith("+251")) {
      return cleaned
    }

    // If starts with 251, add + prefix
    if (cleaned.startsWith("251")) {
      return `+${cleaned}`
    }

    // Otherwise, add +251 prefix
    return `+251${cleaned}`
  }


  const handleConfirm = async () => {
    if (documents === 0) {
      toast.error(t("selectNumberOfDocuments"))
      return
    }
    const provider = detectPaymentProvider()
    const autoSelect = shouldAutoSelectPaymentMethod(provider)

    // Only require payment method selection when not auto-selected
    // CBE Birr Plus / CBE SuperApp / M-Pesa / Telebirr SuperApp: in-app; browser C2B: Telebirr + CBE Birr USSD picker
    if (mode !== "reschedule" && !autoSelect && !selectedPaymentMethod) {
      toast.error(t("selectPaymentMethod"))
      return
    }
    await addAppointmentMutation.mutateAsync()
  }





  const [mounted, setMounted] = useState(false)
  useEffect(() => setMounted(true), [])

  if (!mounted) return null

  if (bookedSuccessfully) {
    return createPortal(
      <div className="fixed inset-0 bg-black/15 backdrop-blur-sm z-[9999] flex items-center justify-center p-4">
        <div
          className="relative w-[400px] max-w-[92vw] rounded-2xl bg-white p-8 shadow-2xl border border-gray-100 text-center"
          onClick={(e) => e.stopPropagation()}
        >
          <CheckCircle2 className="mx-auto mb-4 h-12 w-12 text-pink-600" />
          <h3 className="text-lg font-extrabold text-gray-900">
            {t("appointmentBookedSuccessfully")}
          </h3>
        </div>
      </div>,
      document.body
    )
  }

  return createPortal(
    <div className="fixed inset-0 bg-black/15 backdrop-blur-sm z-[9999] flex items-center justify-center p-4 animate-in fade-in duration-200" onClick={() => setSummaryOpen(false)}>
      {/* <div className="absolute inset-0 bg-black/45 backdrop-blur-sm" /> */}
      <div
        className="relative w-[400px] max-w-[92vw] rounded-2xl bg-white p-4 xs:p-5 shadow-2xl border border-gray-100 animate-in zoom-in-95 duration-200"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="text-center text-base sm:text-[18px] font-extrabold text-gray-900 mb-3 sm:mb-4">
          {mode === "reschedule" ? t("reschedulingSummary") : t("bookingSummary")}
        </h3>

        {mode !== "reschedule" && (
          <div
            className={`mb-3 rounded-xl px-3 py-2 text-center text-xs font-semibold border ${
              paymentProvider === "cbe_birr"
                ? "bg-indigo-50 border-indigo-200 text-indigo-800"
                : paymentProvider === "cbe"
                ? "bg-emerald-50 border-emerald-200 text-emerald-800"
                : paymentProvider === "mpesa"
                  ? "bg-green-50 border-green-200 text-green-800"
                  : paymentProvider === "telebirr"
                    ? "bg-sky-50 border-sky-200 text-sky-800"
                    : "bg-gray-50 border-gray-200 text-gray-700"
            }`}
          >
            {/* {t("openedIn")}: {t(getOpenedInLabelKey(paymentProvider))} */}
          </div>
        )}

        <div className="space-y-3 sm:space-y-4 text-sm">
          {bookingTab == "other" && (<Row label={t("fullName")} value={`${appointerUser.firstname} ${appointerUser.lastname}`} />)}
          <Row label={t("branch")} value={(t(booking.branch_name || "").toString())} />
          <Row label={t("service")} value={(t(booking.service_name || "").toString())} />

          <div className="my-4 border-t border-dashed border-gray-200" />

          <div className="flex items-center justify-between bg-gray-50 p-3 rounded-xl border border-gray-100">
            <span className="text-gray-500 font-medium">{t("bookingDate")}</span>
            <div className="text-right">
              <div className="font-bold text-gray-900">{dateTop}</div>
              <div className="text-xs text-gray-500 font-medium mt-0.5">{dateBottom}</div>
            </div>
          </div>

          <div className="flex items-center justify-between bg-gray-50 p-3 rounded-xl border border-gray-100">
            <span className="text-gray-500 font-medium">{t("bookingTime")}</span>
            <div className="text-right">
              <div className="font-bold text-gray-900">{timeTop}</div>
              <div className="text-xs text-gray-500 font-medium mt-0.5">{timeBottom}</div>
            </div>
          </div>
        </div>

        <div className="mt-4 sm:mt-6 rounded-2xl bg-pink-50 p-4 shadow-sm border border-pink-100 text-gray-900 relative overflow-hidden">
          <div className="absolute top-0 right-0 w-32 h-32 bg-pink-100 rounded-full -mr-10 -mt-10 blur-2xl" />
          <div className="relative z-10">
            <div className="text-pink-600 text-xs font-bold uppercase tracking-wider mb-1">{t("priceSummary")}</div>
            <div className="flex items-end justify-between">
              <span className="text-sm font-medium text-gray-600">{t("totalServiceCharge")}</span>
              <div className="flex items-baseline gap-1">
                {mode === "reschedule" ? (
                  <span className="font-extrabold text-2xl text-gray-900">{t("free")}</span>
                ) : (
                  <>
                    <span className="font-extrabold text-2xl text-gray-900">{paymentProvider === "mpesa" ? mpesaAmount : fixedAmount}</span>
                    <span className="text-sm font-bold text-pink-600">{t("ETB")}</span>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="mt-4 sm:mt-6">
          <div className="text-xs font-bold text-gray-900 uppercase tracking-wider mb-2 sm:mb-3">{t("Numberofcases")}</div>
          <div className={`grid ${isPLC ? "grid-cols-2" : "grid-cols-3"} gap-2 sm:gap-3`}>
            {(isPLC ? [1, 2] : [1, 2, 3]).map((n) => (
              <button
                key={n}
                onClick={() => setDocuments(n)}
                className={`
                  h-12 sm:h-14 rounded-xl border-2 flex flex-col items-center justify-center transition-all duration-200
                  ${documents === n
                    ? "border-pink-500 bg-pink-50 text-pink-700 shadow-sm scale-[1.02]"
                    : "border-gray-100 bg-white text-gray-600 hover:border-pink-200 hover:bg-gray-50"
                  }
                `}
              >
                <div className="text-lg font-bold leading-none">{n}</div>
                <div className="text-[10px] font-medium opacity-80">{n === 1 ? t("document") : t("documents")}</div>
              </button>
            ))}
          </div>
          <div className="mt-3 flex items-start gap-3 p-1 md:p-3 rounded-xl bg-orange-50 border border-orange-100/50">
            <AlertCircle className="w-4 h-4 text-orange-500 flex-shrink-0 mt-0.5" />
            <p className="text-xs text-orange-800 font-medium leading-relaxed">
              {isPLC ? t("moreThanTwoDocumentsWarning") : t("moreThanThreeDocumentsWarning")}
            </p>
          </div>
        </div>

        {(() => {
          if (typeof window === 'undefined') return null

          const provider = detectPaymentProvider()
          const shouldHidePaymentSelection = shouldAutoSelectPaymentMethod(provider)

          // Hide picker for in-app hosts (CBE Birr Plus, CBE SuperApp, M-Pesa, Telebirr SuperApp)
          if (shouldHidePaymentSelection) return null

          const browserMethods = paymentOptions?.filter(isBrowserC2bPaymentMethod) || []
          
          return (
            <div className="mt-4 sm:mt-6">
              {mode !== "reschedule" && (
                <>
                  <div className="text-xs font-bold text-gray-900 uppercase tracking-wider mb-2 sm:mb-3">{t("Select Payment Method")}</div>

                  {paymentLoading ? (
                    <div className="grid grid-cols-2 gap-2 sm:gap-3">
                      <div className="h-14 sm:h-16 rounded-xl bg-gray-100 animate-pulse" />
                      <div className="h-14 sm:h-16 rounded-xl bg-gray-100 animate-pulse" />
                    </div>
                  ) : (
                    <div className="grid grid-cols-2 gap-2 sm:gap-3">
                      {browserMethods.map((method: any) => (
                        <button
                          key={method.id}
                          onClick={() => setSelectedPaymentMethod(method.id)}
                          className={`
                            h-14 sm:h-16 rounded-xl border-2 flex flex-col items-center justify-center transition-all duration-200 relative overflow-hidden
                            ${selectedPaymentMethod === method.id
                              ? "border-pink-500 bg-pink-50 shadow-sm scale-[1.02]"
                              : "border-gray-100 bg-white hover:border-pink-200 hover:bg-gray-50"
                            }
                          `}
                        >
                          {selectedPaymentMethod === method.id && (
                            <div className="absolute top-2 right-2 w-2 h-2 bg-pink-500 rounded-full" />
                          )}
                          {method.icon_url ? (
                            <img
                              src={method.icon_url}
                              alt={method.name}
                              className="w-8 h-8 object-contain mb-1"
                            />
                          ) : (
                            <div className="w-8 h-8 bg-gray-200 rounded-full mb-1" />
                          )}
                          <div className="text-xs font-bold text-gray-700">{t(method.name)}</div>
                        </button>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          )
        })()}

        <div className="flex gap-3 mt-6 sm:mt-8 pt-4 border-t border-gray-100">
          <button
            onClick={() => setSummaryOpen(false)}
            className="flex-1 h-10 sm:h-12 rounded-full bg-white border border-gray-200 text-gray-700 text-sm font-bold hover:bg-gray-50 hover:border-gray-300 transition-all duration-200 active:scale-95"
          >
            {t("cancel")}
          </button>
          <button
            onClick={handleConfirm}
            disabled={addAppointmentMutation.isPending}
            className="flex-1 h-10 sm:h-12 rounded-full bg-pink-600 text-white text-sm font-bold shadow-lg shadow-pink-200 hover:bg-pink-700 hover:shadow-pink-300 hover:-translate-y-0.5 transition-all duration-200 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none disabled:shadow-none flex items-center justify-center gap-2"
          >
            {addAppointmentMutation.isPending && (
              <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            )}
            {addAppointmentMutation.isPending ? t("processing") : (mode === "reschedule" ? t("confirmReschedule") : t("confirmAndPay"))}
          </button>
        </div>
      </div>
    </div>,
    document.body
  )
}


function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between py-1">
      <span className="text-gray-500 font-medium">{label}</span>
      <span className="font-bold text-gray-900 text-right max-w-[60%] truncate">{value}</span>
    </div>
  )
}